import { Box, Card, CardContent, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import Logo from '../layout/Logo'

export type AuthShellProps = {
  title: ReactNode
  subtitle?: ReactNode
  /** Optional status glyph above the title (e.g. an error/success icon). */
  icon?: ReactNode
  children?: ReactNode
  /** Links under the card body (back to login, sign up). */
  footer?: ReactNode
}

/**
 * The one frame every signed-out screen (login, forgot/reset password,
 * email verification) renders in. Each screen used to build its own card:
 * login was a 300px react-admin card pinned 6em from the top under a gray
 * lock avatar, the others a centered 400/500px card with a 34px purple — or
 * red, or green — headline. Same product, four different front doors.
 *
 * State (error/success) is carried by `icon` and the Alert inside `children`,
 * never by coloring the title: a red headline is a louder signal than an
 * expired reset link deserves (UI-STYLE-GUIDE §1).
 */
export const AuthShell = ({ title, subtitle, icon, children, footer }: AuthShellProps) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: { xs: 4, sm: 6 },
      bgcolor: 'background.default',
    }}
  >
    <Card
      data-testid="auth-shell"
      sx={{
        width: '100%',
        maxWidth: 400,
        borderRadius: 3,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.06)',
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Logo width={130} height={30} />
        </Box>
        {icon && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>{icon}</Box>}
        <Typography variant="h5" component="h1" textAlign="center" sx={{ mb: subtitle ? 1 : 3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            {subtitle}
          </Typography>
        )}
        {children}
        {footer && (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {footer}
          </Box>
        )}
      </CardContent>
    </Card>
  </Box>
)

export default AuthShell
